import {useDebounce} from '@github-ui/use-debounce'
import {PlusIcon, TriangleDownIcon} from '@primer/octicons-react'
import {Button, SelectPanel, type SelectPanelItemInput} from '@primer/react'
import {type RefObject, useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {ActorAvatar} from './ActorAvatar'
import {ALL_CATEGORIES_SENTINEL} from './CategoryFilter'
import {actorTypeString} from './helpers/actor-type-string'
import {buildActorDescription} from './helpers/build-actor-description'
import {humanizeRoleName} from './helpers/humanize-role-name'
import {type Actor, actorKey, type ActorSelectorSource} from './types'

const SUGGESTION_DEBOUNCE_MS = 300

export interface ActorSelectPanelProps<TActor extends Actor> {
  selector: ActorSelectorSource<TActor>
  /** Currently selected actors (used to render the toggled-on state). */
  selectedActors: TActor[]
  selectedIds: Set<string>
  baseAvatarUrl?: string
  categoryFilter: string
  /** When true, suggestions are not expected to carry category metadata. */
  categoriesAvailable: boolean
  onAdd: (actor: TActor) => void
  onRemove: (actor: TActor) => void
  /** Anchor ref so callers can refocus the trigger button after row removal. */
  anchorRef?: RefObject<HTMLButtonElement | null>
}

export function ActorSelectPanel<TActor extends Actor>({
  selector,
  selectedActors,
  selectedIds,
  baseAvatarUrl,
  categoryFilter,
  categoriesAvailable,
  onAdd,
  onRemove,
  anchorRef,
}: ActorSelectPanelProps<TActor>) {
  const [open, setOpen] = useState(false)
  const [filter, setFilter] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [suggestions, setSuggestions] = useState<TActor[]>([])

  const fetchSuggestions = selector.fetchSuggestions

  const loadSuggestions = useCallback(
    async (query: string) => {
      setIsLoading(true)
      try {
        const result = await fetchSuggestions(query)
        setSuggestions(result)
      } catch {
        setSuggestions([])
      } finally {
        setIsLoading(false)
      }
    },
    [fetchSuggestions],
  )

  const debouncedLoad = useDebounce(loadSuggestions, SUGGESTION_DEBOUNCE_MS, {onChangeBehavior: 'cancel'})

  const visibleSuggestions = useMemo(() => {
    if (categoryFilter === ALL_CATEGORIES_SENTINEL) return suggestions
    return suggestions.filter(s => s.actorCategory === categoryFilter)
  }, [suggestions, categoryFilter])

  const includeCategoryInDescription = categoryFilter === ALL_CATEGORIES_SENTINEL

  const items: SelectPanelItemInput[] = useMemo(() => {
    return visibleSuggestions.map(suggestion => {
      const id = actorKey(suggestion)
      return {
        id,
        text: humanizeRoleName(suggestion),
        description: buildActorDescription({
          category: suggestion.actorCategory,
          includeCategory: includeCategoryInDescription,
          fallbackTypeLabel: categoriesAvailable ? undefined : actorTypeString(suggestion.actorType) || undefined,
          teamHandle: suggestion.actorType === 'Team' ? suggestion.name : undefined,
          integrationOwner: suggestion.actorType === 'Integration' ? suggestion.owner : undefined,
        }),
        leadingVisual: () => <ActorAvatar actor={suggestion} baseUrl={baseAvatarUrl} />,
        onAction: () => {
          if (selectedIds.has(id)) {
            // Find the matching actor (prefer the cached suggestion shape).
            const existing = selectedActors.find(a => actorKey(a) === id) ?? suggestion
            onRemove(existing)
          } else {
            onAdd(suggestion)
          }
        },
      }
    })
  }, [
    visibleSuggestions,
    baseAvatarUrl,
    includeCategoryInDescription,
    categoriesAvailable,
    selectedIds,
    selectedActors,
    onAdd,
    onRemove,
  ])

  const selectedItems = useMemo(() => items.filter(item => selectedIds.has(item.id as string)), [items, selectedIds])

  // Resolve / cleanup the debounce when filter changes via `onFilterChange`.
  // Initial fetch happens on open.
  const onOpenChange = useCallback(async () => {
    if (!open) {
      setFilter('')
      setOpen(true)
      await loadSuggestions('')
    } else {
      setOpen(false)
    }
  }, [open, loadSuggestions])

  // Cancel pending debounced fetches if the panel closes.
  useEffect(() => {
    if (!open) {
      debouncedLoad.cancel()
    }
  }, [open, debouncedLoad])

  const fallbackAnchorRef = useRef<HTMLButtonElement>(null)
  const effectiveAnchorRef = anchorRef ?? fallbackAnchorRef

  const addButtonLabel = selector.addButtonLabel ?? 'Add actors'
  const activeCategory = categoryFilter === ALL_CATEGORIES_SENTINEL ? null : categoryFilter
  const panelTitle = selector.panelTitle
    ? selector.panelTitle(activeCategory)
    : activeCategory
      ? `Add ${activeCategory} actors`
      : addButtonLabel

  return (
    <>
      <Button
        ref={effectiveAnchorRef}
        leadingVisual={PlusIcon}
        trailingVisual={TriangleDownIcon}
        aria-label={addButtonLabel}
        onClick={() => {
          void onOpenChange()
        }}
      >
        {addButtonLabel}
      </Button>
      <SelectPanel
        anchorRef={effectiveAnchorRef}
        renderAnchor={null}
        title={panelTitle}
        subtitle={selector.panelSubtitle}
        loading={isLoading}
        open={open}
        onOpenChange={onOpenChange}
        items={items}
        selected={selectedItems}
        // Selection is driven by per-item `onAction` so this is a no-op.
        onSelectedChange={() => {}}
        placeholderText={selector.panelPlaceholder ?? 'Filter actors'}
        filterValue={filter}
        onFilterChange={f => {
          setFilter(f)
          setIsLoading(true)
          void debouncedLoad(f)
        }}
        overlayProps={{
          width: 'medium',
          height: 'medium',
        }}
        message={
          !isLoading && items.length === 0
            ? {
                variant: 'empty',
                title: 'No suggestions',
                body: 'Adjust your search term to find other actors',
              }
            : undefined
        }
      />
    </>
  )
}
